import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, ArrowRight, User, ChevronDown, Truck } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Reveal, MaskLine } from "@/components/Reveal";
import { CustomerAuthForms } from "@/components/CustomerAuthForms";
import { createCheckout, createPayPalCheckout, getPaymentsConfig } from "@/api";
import Seo from "@/components/Seo";

const EMPTY_ADDR = { full_name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "", phone: "", courier_note: "" };
const REQUIRED = ["full_name", "line1", "city", "postal_code", "country"];
const isEmail = (v) => /^\S+@\S+\.\S+$/.test(v || "");
const INPUT_CLS = "mt-2 w-full bg-transparent border-b border-line py-2.5 font-light focus:outline-none focus:border-ink transition-colors";

function AddrField({ k, label, value, onChange, required, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{label}{required ? " *" : ""}</label>
      <input type={type} value={value} onChange={onChange} data-testid={`ship-${k}`} className={INPUT_CLS} placeholder={placeholder} />
    </div>
  );
}

export default function Payment() {
  const { cart, t, customer, lang } = useApp();
  const [loading, setLoading] = useState("");
  const [config, setConfig] = useState({ paypal_enabled: false, tax_enabled: false });
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [guestEmail, setGuestEmail] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getPaymentsConfig().then(setConfig).catch(() => {});
  }, []);

  // Prefill saved address for signed-in customers
  useEffect(() => {
    if (customer?.default_address) setAddr((a) => ({ ...EMPTY_ADDR, ...customer.default_address }));
  }, [customer]);

  const usdTotal = (
    cart.reduce((s, p) => s + Math.round((p.price_rub / 90) * 100) * (p.quantity || 1), 0) / 100
  ).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const items = () => cart.map((p) => ({ id: p.id, quantity: p.quantity || 1, size: p.size || null }));
  const lineUsd = (p) =>
    (Math.round((p.price_rub / 90) * 100) * (p.quantity || 1) / 100)
      .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const upd = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));
  const f = t.payment.fields;
  const addrValid = REQUIRED.every((k) => (addr[k] || "").trim());
  const emailValid = customer ? true : isEmail(guestEmail);
  const canPay = cart.length > 0 && addrValid && emailValid && !loading;

  const extra = () => ({ shipping: addr, email: customer ? undefined : guestEmail, lang });

  const guardMsg = () => {
    if (!addrValid) return t.payment.guardAddress;
    if (!emailValid) return t.payment.guardEmail;
    return "";
  };

  const payCard = async () => {
    if (!canPay) { toast.error(guardMsg()); return; }
    setLoading("card");
    try {
      const data = await createCheckout(items(), window.location.origin, extra());
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.payment.failedMsg);
      setLoading("");
    }
  };

  const payPaypal = async () => {
    if (!canPay) { toast.error(guardMsg()); return; }
    setLoading("paypal");
    try {
      const data = await createPayPalCheckout(items(), window.location.origin, extra());
      if (!data.approval_url) throw new Error("no approval url");
      window.location.href = data.approval_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.payment.failedMsg);
      setLoading("");
    }
  };

  const inputCls = INPUT_CLS;

  return (
    <div className="pt-32 md:pt-40 pb-24 px-5 md:px-10" data-testid="payment-page">
      <Seo title={t.seo.checkout.title} description={t.seo.checkout.description} noindex />
      <header className="mb-10 md:mb-14">
        <Reveal><p className="text-xs uppercase tracking-[0.28em] text-muted mb-3 md:mb-6">{t.hero.eyebrow}</p></Reveal>
        <h1 className="font-display font-light text-[20vw] lg:text-[12rem] leading-[0.8] tracking-tighter">
          <MaskLine delay={0.1}>{t.payment.title}</MaskLine>
        </h1>
      </header>

      {cart.length === 0 ? (
        <Reveal>
          <div className="border-y border-line py-16 text-center">
            <p className="font-display text-2xl mb-4">{t.payment.empty}</p>
            <Link to="/catalogue" className="text-xs uppercase tracking-[0.2em] link-underline" data-testid="payment-empty-shop">{t.payment.backToShop}</Link>
          </div>
        </Reveal>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: account + shipping */}
          <div className="lg:col-span-7 space-y-12">
            {/* 1. Account */}
            <section data-testid="checkout-account">
              <p className="text-xs uppercase tracking-[0.24em] text-muted mb-4">{t.payment.accountStep}</p>
              {customer ? (
                <div className="flex items-center gap-3 border border-line px-4 py-3.5" data-testid="checkout-signed-in">
                  <User className="h-4 w-4 text-accent" />
                  <span className="text-sm">{t.payment.signedInAs} <strong>{customer.email}</strong></span>
                </div>
              ) : (
                <div className="border border-line">
                  <button
                    type="button"
                    onClick={() => setShowAuth((v) => !v)}
                    data-testid="checkout-toggle-auth"
                    className="flex items-center justify-between w-full px-4 py-3.5 text-left"
                  >
                    <span className="text-sm">{t.payment.authToggle} <span className="text-muted">{t.payment.authToggleHint}</span></span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showAuth ? "rotate-180" : ""}`} />
                  </button>
                  {showAuth && (
                    <div className="px-4 pb-6 pt-2 border-t border-line">
                      <CustomerAuthForms redirectPath="/payment" onSuccess={() => setShowAuth(false)} />
                    </div>
                  )}
                  {!showAuth && (
                    <p className="px-4 pb-3 -mt-1 text-[11px] text-muted">{t.payment.guestNote}</p>
                  )}
                </div>
              )}
            </section>

            {/* 2. Shipping address */}
            <section data-testid="checkout-shipping">
              <p className="text-xs uppercase tracking-[0.24em] text-muted mb-4">{t.payment.shippingStep}</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2"><AddrField k="full_name" label={f.full_name} value={addr.full_name} onChange={upd("full_name")} required placeholder={f.full_namePh} /></div>
                {!customer && (
                  <div className="sm:col-span-2">
                    <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{f.email} *</label>
                    <input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} data-testid="ship-email" className={inputCls} placeholder={f.emailPh} />
                    <p className="text-[10px] text-muted mt-1">{t.payment.emailConfirmNote}</p>
                  </div>
                )}
                <div className="sm:col-span-2"><AddrField k="line1" label={f.line1} value={addr.line1} onChange={upd("line1")} required placeholder={f.line1Ph} /></div>
                <div className="sm:col-span-2"><AddrField k="line2" label={f.line2} value={addr.line2} onChange={upd("line2")} placeholder={f.line2Ph} /></div>
                <AddrField k="city" label={f.city} value={addr.city} onChange={upd("city")} required placeholder={f.cityPh} />
                <AddrField k="state" label={f.state} value={addr.state} onChange={upd("state")} placeholder={f.statePh} />
                <AddrField k="postal_code" label={f.postal_code} value={addr.postal_code} onChange={upd("postal_code")} required placeholder={f.postal_codePh} />
                <AddrField k="country" label={f.country} value={addr.country} onChange={upd("country")} required placeholder={f.countryPh} />
                <div className="sm:col-span-2"><AddrField k="phone" label={f.phone} value={addr.phone} onChange={upd("phone")} type="tel" placeholder={f.phonePh} /></div>
                <div className="sm:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{f.courier_note}</label>
                  <textarea value={addr.courier_note} onChange={upd("courier_note")} data-testid="ship-courier_note" rows={2} className={`${INPUT_CLS} resize-none`} placeholder={f.courier_notePh} />
                </div>
              </div>
            </section>
          </div>

          {/* Right: summary + pay */}
          <div className="lg:col-span-5 lg:pl-10 lg:border-l border-line">
            <p className="text-xs uppercase tracking-[0.24em] text-muted mb-4">{t.payment.summary}</p>
            <div className="divide-y divide-line border-y border-line mb-6" data-testid="checkout-items">
              {cart.map((p) => (
                <div key={p.key} className="flex items-center gap-4 py-4" data-testid={`checkout-item-${p.key}`}>
                  <img src={p.image} alt={p.name} className="h-16 w-14 object-cover bg-surface" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-muted">{p.brand}</p>
                    <h3 className="font-display text-lg truncate">{p.name}</h3>
                    <p className="text-xs text-ink/50 mt-1">
                      {p.size ? `${t.cart.size}: ${p.size} · ` : ""}{t.payment.qty}: {p.quantity || 1}
                    </p>
                  </div>
                  <span className="text-sm whitespace-nowrap" data-testid={`checkout-line-total-${p.key}`}>${lineUsd(p)}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-muted">{t.payment.total}</span>
              <span className="font-display text-4xl" data-testid="checkout-total">${usdTotal}</span>
            </div>
            {config.tax_enabled && (
              <p className="text-[10px] text-muted mb-6" data-testid="tax-note">{t.payment.taxNote}</p>
            )}
            <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-ink/50 mb-6 mt-2" data-testid="checkout-shipping-included">
              <Truck className="h-3.5 w-3.5" strokeWidth={1.5} /> {t.shop.shippingIncludedFull}
            </p>
            <div>
              <motion.button
                onClick={payCard}
                disabled={!canPay}
                data-testid="pay-button"
                whileTap={{ scale: 0.99 }}
                className="group flex items-center justify-center gap-3 w-full bg-ink text-paper py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-accent hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CreditCard className="h-4 w-4" />
                {loading === "card" ? t.payment.processingBtn : t.payment.payButton}
                {loading !== "card" && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
              </motion.button>

              {config.paypal_enabled && (
                <motion.button
                  onClick={payPaypal}
                  disabled={!canPay}
                  data-testid="paypal-button"
                  whileTap={{ scale: 0.99 }}
                  className="flex items-center justify-center gap-2 w-full border border-ink py-4 mt-3 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-[#003087] hover:text-white hover:border-[#003087] disabled:opacity-50 disabled:cursor-not-allowed disabled:border-line disabled:text-muted disabled:hover:bg-transparent"
                >
                  {loading === "paypal" ? t.payment.processingBtn : t.payment.payPaypal}
                </motion.button>
              )}
            </div>
            {!canPay && !loading && (
              <p className="text-[10px] text-muted text-center mt-3" data-testid="checkout-guard">
                {t.payment.guardComplete}
              </p>
            )}
            <p className="text-[10px] text-muted text-center mt-3 font-light">{t.payment.secure}</p>
          </div>
        </div>
      )}
    </div>
  );
}
